"use client";

/**
 * TD-VIDEO-PATH-DISCLOSURE — UX hint inline com path host do inbox de vídeo.
 *
 * Vídeos entram no pipeline ML via flat-drop na pasta inbox configurada no
 * servidor (VIDEO_INBOX_PATH env, default /data/inbox). Worker (watcher.scan_inbox)
 * varre a cada ~10s, hasheia (sha256) e cria Video rows com status='detected' +
 * levantamento_id NULL — só depois user agrupa em Levantamento.
 *
 * #299 G6 — path vem de GET /videos/config (backend env) em vez de ser
 * chumbado no componente. N VMs têm paths diferentes; o servidor sabe o seu.
 */

import { useState } from "react";
import { toast } from "sonner";

import { useVideoConfig } from "@/lib/api-video";

export interface VideoInboxHintProps {
  /** "default" = full hint card. "compact" = inline single-line variant
   * (usado no rodapé da página de Levantamento). */
  variant?: "default" | "compact";
}

export function VideoInboxHint({ variant = "default" }: VideoInboxHintProps) {
  const [copied, setCopied] = useState<string | null>(null);
  const { data: config } = useVideoConfig();

  const inboxPath = config?.inbox_path ?? "…carregando path…";

  async function copyPath(p: string) {
    if (p.includes("…")) return; // ainda carregando
    try {
      await navigator.clipboard.writeText(p);
      setCopied(p);
      toast.success("Path copiado");
      setTimeout(() => setCopied((curr) => (curr === p ? null : curr)), 2000);
    } catch {
      toast.error("Falha ao copiar path");
    }
  }

  if (variant === "compact") {
    return (
      <div
        data-testid="video-inbox-hint-compact"
        className="rounded-md border border-tide-border bg-tide-bg-secondary px-3 py-2 text-[11px] text-tide-text-secondary"
      >
        <span className="material-symbols-outlined align-middle text-tide-accent text-sm mr-1.5">
          info
        </span>
        Vídeos chegam via flat-drop em{" "}
        <code className="rounded bg-tide-bg-hover px-1 font-mono">
          data/inbox/
        </code>{" "}
        (worker detecta a cada ~10s). Path host:{" "}
        <code className="rounded bg-tide-bg-hover px-1 font-mono">
          {inboxPath}
        </code>
        <button
          type="button"
          onClick={() => copyPath(inboxPath)}
          aria-label="Copiar path inbox"
          data-testid="copy-compact-inbox"
          className="ml-1 align-middle rounded p-0.5 text-tide-text-muted hover:bg-tide-bg-hover hover:text-tide-text-primary transition-colors"
        >
          <span className="material-symbols-outlined text-xs align-middle">
            {copied === inboxPath ? "check" : "content_copy"}
          </span>
        </button>
      </div>
    );
  }

  return (
    <div
      data-testid="video-inbox-hint"
      className="rounded-lg border border-tide-border bg-tide-bg-secondary p-4"
    >
      <div className="flex items-start gap-3">
        <span className="material-symbols-outlined text-tide-accent text-lg mt-0.5">
          folder_open
        </span>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-tide-text-primary">
            Como adicionar vídeo
          </h3>
          <p className="mt-1 text-xs text-tide-text-secondary">
            <strong>Com acesso ao host:</strong> solte arquivos{" "}
            <code className="rounded bg-tide-bg-hover px-1 font-mono text-[10px]">
              .mp4 / .mov / .m4v
            </code>{" "}
            na pasta inbox do host. Worker detecta a cada ~10s, hasheia (dedupe
            por sha256) e cria registros automaticamente. Depois você agrupa em
            um Levantamento.
          </p>
          <p className="mt-1.5 text-xs text-tide-text-secondary">
            <strong>Sem acesso ao host</strong> (operador remoto): use{" "}
            <strong>Novo Levantamento</strong> ou a{" "}
            <strong>Página de upload</strong> pra enviar o vídeo direto do
            navegador — o flat-drop acima não te alcança.
          </p>
          <div className="mt-3 flex items-center gap-2">
            <span className="w-20 shrink-0 text-[10px] font-medium uppercase tracking-wide text-tide-text-muted">
              Inbox
            </span>
            <code className="flex-1 truncate rounded bg-tide-bg-hover px-2 py-1 font-mono text-[11px] text-tide-text-primary">
              {inboxPath}
            </code>
            <button
              type="button"
              onClick={() => copyPath(inboxPath)}
              data-testid="copy-inbox-path"
              aria-label="Copiar path inbox"
              className="rounded p-1 text-tide-text-muted hover:bg-tide-bg-hover hover:text-tide-text-primary transition-colors"
            >
              <span className="material-symbols-outlined text-sm">
                {copied === inboxPath ? "check" : "content_copy"}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
